import { useEffect } from 'react'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Alert, CircularProgress, Divider, Typography, Checkbox, FormControlLabel, IconButton, Box, Autocomplete,
} from '@mui/material'
import { Add, DeleteOutlined } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createArticle, updateArticle, getArticle, listUnits, listArticles,
  type ArticleListItem, type SaveArticlePayload,
} from '../../api/nomenclatoare'
import { listVatRates } from '../../api/taxe'
import { errorMessage } from '../../api/client'

const enumOpt = <T extends string>() => z.union([z.custom<T>(), z.literal('')]).optional()

const schema = z.object({
  code: z.string().min(1, 'Câmp obligatoriu').max(64),
  oldCode: z.string().optional(),
  shortDescription: z.string().min(1, 'Câmp obligatoriu').max(256),
  longDescription: z.string().optional(),
  type: z.enum(['ProdusFinit', 'Semifabricat', 'MateriePrima', 'Ambalaj', 'Serviciu', 'Set']),
  family: enumOpt<'Pahar' | 'Caserola' | 'Cheseta' | 'Capac' | 'Platina' | 'Insert' | 'Altele'>(),
  marketSegment: enumOpt<'Lapte' | 'Delicatese' | 'Blistere' | 'Altele'>(),
  material: enumOpt<'PS' | 'PP' | 'Alu' | 'Altele'>(),
  diameterMm: z.string().optional(), volumeMl: z.string().optional(), weightG: z.string().optional(), unitWeightG: z.string().optional(),
  primaryUnitOfMeasureId: z.string().min(1, 'Selectați UM principală'),
  secondaryUnitOfMeasureId: z.string().optional(),
  secondaryConversionFactor: z.string().optional(),
  unitsPerBox: z.string().optional(), boxesPerPallet: z.string().optional(), standardCost: z.string().optional(),
  minMarginPercent: z.string().optional(),
  vatRateId: z.string().optional(),
  customsCode: z.string().optional(),
  requiresLotTraceability: z.boolean(),
  requireCompleteSetDelivery: z.boolean(),
  minStockQuantity: z.string().optional(),
  unitConversions: z.array(z.object({
    unitOfMeasureId: z.string().min(1, 'Selectați UM'),
    factorToPrimary: z.string().min(1, 'Câmp obligatoriu'),
  })),
  setComponents: z.array(z.object({
    componentArticleId: z.string().min(1, 'Selectați articol'),
    quantity: z.string().min(1, 'Câmp obligatoriu'),
  })),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = {
  code: '', oldCode: '', shortDescription: '', longDescription: '', type: 'ProdusFinit',
  family: '', marketSegment: '', material: '',
  diameterMm: '', volumeMl: '', weightG: '', unitWeightG: '',
  primaryUnitOfMeasureId: '', secondaryUnitOfMeasureId: '', secondaryConversionFactor: '',
  unitsPerBox: '', boxesPerPallet: '', standardCost: '', minMarginPercent: '',
  vatRateId: '', customsCode: '', requiresLotTraceability: false, requireCompleteSetDelivery: false,
  minStockQuantity: '', unitConversions: [], setComponents: [],
}

const FAMILIES = ['Pahar', 'Caserola', 'Cheseta', 'Capac', 'Platina', 'Insert', 'Altele'] as const
const SEGMENTS = ['Lapte', 'Delicatese', 'Blistere', 'Altele'] as const
const MATERIALS = ['PS', 'PP', 'Alu', 'Altele'] as const

function numOrNull(v: string | undefined): number | null {
  if (v === undefined || v === '') return null
  const n = Number(v)
  return Number.isNaN(n) ? null : n
}

export default function ArticleFormDialog({ article, defaultToSet, onClose }: {
  article: ArticleListItem | null | 'new'; defaultToSet?: boolean; onClose: () => void
}) {
  const open = article !== null
  const editing = article !== null && article !== 'new'
  const qc = useQueryClient()

  const { data: units } = useQuery({ queryKey: ['nomenclatoare', 'um'], queryFn: listUnits, enabled: open })
  const { data: vatRates } = useQuery({ queryKey: ['financiar', 'cote-tva'], queryFn: listVatRates, enabled: open })
  const { data: allArticles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })
  const { data: detail } = useQuery({
    queryKey: ['nomenclatoare', 'articol', editing ? (article as ArticleListItem).id : null],
    queryFn: () => getArticle((article as ArticleListItem).id),
    enabled: editing,
  })

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY,
  })
  const type = watch('type')

  const unitConversions = useFieldArray({ control, name: 'unitConversions' })
  const setComponents = useFieldArray({ control, name: 'setComponents' })

  useEffect(() => {
    if (editing && detail) {
      reset({
        code: detail.code, oldCode: detail.oldCode ?? '', shortDescription: detail.shortDescription,
        longDescription: detail.longDescription ?? '', type: detail.type,
        family: detail.family ?? '', marketSegment: detail.marketSegment ?? '', material: detail.material ?? '',
        diameterMm: detail.diameterMm != null ? String(detail.diameterMm) : '',
        volumeMl: detail.volumeMl != null ? String(detail.volumeMl) : '',
        weightG: detail.weightG != null ? String(detail.weightG) : '',
        unitWeightG: detail.unitWeightG != null ? String(detail.unitWeightG) : '',
        primaryUnitOfMeasureId: detail.primaryUnitOfMeasureId, secondaryUnitOfMeasureId: detail.secondaryUnitOfMeasureId ?? '',
        secondaryConversionFactor: detail.secondaryConversionFactor != null ? String(detail.secondaryConversionFactor) : '',
        unitsPerBox: detail.unitsPerBox != null ? String(detail.unitsPerBox) : '',
        boxesPerPallet: detail.boxesPerPallet != null ? String(detail.boxesPerPallet) : '',
        standardCost: detail.standardCost != null ? String(detail.standardCost) : '',
        minMarginPercent: detail.minMarginPercent != null ? String(detail.minMarginPercent) : '',
        vatRateId: detail.vatRateId ?? '', customsCode: detail.customsCode ?? '',
        requiresLotTraceability: detail.requiresLotTraceability, requireCompleteSetDelivery: detail.requireCompleteSetDelivery,
        minStockQuantity: detail.minStockQuantity != null ? String(detail.minStockQuantity) : '',
        unitConversions: detail.unitConversions.map((c) => ({ unitOfMeasureId: c.unitOfMeasureId, factorToPrimary: String(c.factorToPrimary) })),
        setComponents: detail.setComponents.map((c) => ({ componentArticleId: c.componentArticleId, quantity: String(c.quantity) })),
      })
    } else if (article === 'new') {
      reset({ ...EMPTY, type: defaultToSet ? 'Set' : 'ProdusFinit' })
    }
  }, [article, editing, detail, defaultToSet, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: SaveArticlePayload = {
        code: values.code, shortDescription: values.shortDescription, type: values.type,
        primaryUnitOfMeasureId: values.primaryUnitOfMeasureId,
        requiresLotTraceability: values.requiresLotTraceability, requireCompleteSetDelivery: values.requireCompleteSetDelivery,
        minStockQuantity: values.minStockQuantity ? Number(values.minStockQuantity) : null,
        oldCode: values.oldCode || null,
        longDescription: values.longDescription || null,
        family: values.family || null,
        marketSegment: values.marketSegment || null,
        material: values.material || null,
        secondaryUnitOfMeasureId: values.secondaryUnitOfMeasureId || null,
        vatRateId: values.vatRateId || null,
        customsCode: values.customsCode || null,
        diameterMm: numOrNull(values.diameterMm), volumeMl: numOrNull(values.volumeMl),
        weightG: numOrNull(values.weightG), unitWeightG: numOrNull(values.unitWeightG),
        secondaryConversionFactor: numOrNull(values.secondaryConversionFactor),
        unitsPerBox: numOrNull(values.unitsPerBox) != null ? Math.trunc(numOrNull(values.unitsPerBox)!) : null,
        boxesPerPallet: numOrNull(values.boxesPerPallet) != null ? Math.trunc(numOrNull(values.boxesPerPallet)!) : null,
        standardCost: numOrNull(values.standardCost),
        minMarginPercent: numOrNull(values.minMarginPercent),
        unitConversions: values.unitConversions.map((c) => ({ unitOfMeasureId: c.unitOfMeasureId, factorToPrimary: Number(c.factorToPrimary) })),
        setComponents: values.setComponents.map((c) => ({ componentArticleId: c.componentArticleId, quantity: Number(c.quantity) })),
      }
      if (editing) return updateArticle((article as ArticleListItem).id, payload)
      return createArticle(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['nomenclatoare', 'articole'] })
      onClose()
    },
  })

  const findArticle = (id: string) => allArticles?.find((a) => a.id === id)
  const componentOptions = allArticles?.filter((a) => a.id !== (editing ? (article as ArticleListItem).id : '')) ?? []

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare articol' : type === 'Set' ? 'Set nou' : 'Articol nou'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Divider><Typography variant="overline" color="text.secondary">General</Typography></Divider>
            <Stack direction="row" spacing={2}>
              <Controller name="code" control={control} render={({ field }) => (
                <TextField {...field} label="Cod" fullWidth error={!!errors.code} helperText={errors.code?.message} />
              )} />
              <Controller name="oldCode" control={control} render={({ field }) => (
                <TextField {...field} label="Cod vechi (alias)" fullWidth />
              )} />
            </Stack>
            <Controller name="shortDescription" control={control} render={({ field }) => (
              <TextField {...field} label="Descriere scurtă" fullWidth error={!!errors.shortDescription} helperText={errors.shortDescription?.message} />
            )} />
            <Controller name="longDescription" control={control} render={({ field }) => (
              <TextField {...field} label="Descriere lungă" fullWidth multiline minRows={2} />
            )} />
            <Stack direction="row" spacing={2}>
              <Controller name="type" control={control} render={({ field }) => (
                <TextField {...field} select label="Tip articol" sx={{ width: 220 }} error={!!errors.type} helperText={errors.type?.message}>
                  <MenuItem value="ProdusFinit">Produs finit</MenuItem>
                  <MenuItem value="Semifabricat">Semifabricat</MenuItem>
                  <MenuItem value="MateriePrima">Materie primă</MenuItem>
                  <MenuItem value="Ambalaj">Ambalaj</MenuItem>
                  <MenuItem value="Serviciu">Serviciu</MenuItem>
                  <MenuItem value="Set">Set</MenuItem>
                </TextField>
              )} />
              <Controller name="family" control={control} render={({ field }) => (
                <TextField {...field} select label="Familie" fullWidth>
                  <MenuItem value="">—</MenuItem>
                  {FAMILIES.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                </TextField>
              )} />
              <Controller name="marketSegment" control={control} render={({ field }) => (
                <TextField {...field} select label="Segment de piață" fullWidth>
                  <MenuItem value="">—</MenuItem>
                  {SEGMENTS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              )} />
            </Stack>

            <Divider><Typography variant="overline" color="text.secondary">Dimensiuni și material</Typography></Divider>
            <Stack direction="row" spacing={2}>
              <Controller name="material" control={control} render={({ field }) => (
                <TextField {...field} select label="Material" fullWidth>
                  <MenuItem value="">—</MenuItem>
                  {MATERIALS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </TextField>
              )} />
              <Controller name="diameterMm" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Diametru (mm)" fullWidth />
              )} />
              <Controller name="volumeMl" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Volum (ml)" fullWidth />
              )} />
              <Controller name="weightG" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Gramaj (g)" fullWidth />
              )} />
              <Controller name="unitWeightG" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Greutate unitară (g)" fullWidth />
              )} />
            </Stack>

            <Divider><Typography variant="overline" color="text.secondary">UM, ambalare și cost</Typography></Divider>
            <Stack direction="row" spacing={2}>
              <Controller name="primaryUnitOfMeasureId" control={control} render={({ field }) => (
                <TextField {...field} select label="UM principală" fullWidth error={!!errors.primaryUnitOfMeasureId} helperText={errors.primaryUnitOfMeasureId?.message}>
                  {units?.map((u) => <MenuItem key={u.id} value={u.id}>{u.name} ({u.code})</MenuItem>)}
                </TextField>
              )} />
              <Controller name="secondaryUnitOfMeasureId" control={control} render={({ field }) => (
                <TextField {...field} select label="UM secundară" fullWidth>
                  <MenuItem value="">—</MenuItem>
                  {units?.map((u) => <MenuItem key={u.id} value={u.id}>{u.name} ({u.code})</MenuItem>)}
                </TextField>
              )} />
              <Controller name="secondaryConversionFactor" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Factor conversie" fullWidth />
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="unitsPerBox" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Buc/cutie" fullWidth />
              )} />
              <Controller name="boxesPerPallet" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Cutii/palet" fullWidth />
              )} />
              <Controller name="standardCost" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Cost standard" fullWidth />
              )} />
              <Controller name="minMarginPercent" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Marjă minimă (%)" fullWidth />
              )} />
              <Controller name="vatRateId" control={control} render={({ field }) => (
                <TextField {...field} select label="Cotă TVA" fullWidth>
                  <MenuItem value="">—</MenuItem>
                  {vatRates?.filter((v) => v.isActive).map((v) => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
                </TextField>
              )} />
              <Controller name="customsCode" control={control} render={({ field }) => (
                <TextField {...field} label="Cod vamal" fullWidth />
              )} />
            </Stack>
            <Stack direction="row" spacing={3}>
              <Controller name="requiresLotTraceability" control={control} render={({ field }) => (
                <FormControlLabel control={<Checkbox checked={field.value} onChange={field.onChange} />} label="Necesită trasabilitate lot" />
              )} />
              {type === 'Set' && (
                <Controller name="requireCompleteSetDelivery" control={control} render={({ field }) => (
                  <FormControlLabel control={<Checkbox checked={field.value} onChange={field.onChange} />} label="Livrare completă obligatorie" />
                )} />
              )}
              <Controller name="minStockQuantity" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Stoc minim (alertare, opțional)" sx={{ width: 220 }} />
              )} />
            </Stack>

            <Divider><Typography variant="overline" color="text.secondary">Conversii UM suplimentare</Typography></Divider>
            <Stack spacing={1}>
              {unitConversions.fields.map((f, i) => (
                <Stack key={f.id} direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
                  <Controller name={`unitConversions.${i}.unitOfMeasureId`} control={control} render={({ field }) => (
                    <TextField {...field} select label="UM" fullWidth
                      error={!!errors.unitConversions?.[i]?.unitOfMeasureId} helperText={errors.unitConversions?.[i]?.unitOfMeasureId?.message}>
                      {units?.map((u) => <MenuItem key={u.id} value={u.id}>{u.name} ({u.code})</MenuItem>)}
                    </TextField>
                  )} />
                  <Controller name={`unitConversions.${i}.factorToPrimary`} control={control} render={({ field }) => (
                    <TextField {...field} type="number" label="Factor față de UM principală" fullWidth
                      error={!!errors.unitConversions?.[i]?.factorToPrimary} helperText={errors.unitConversions?.[i]?.factorToPrimary?.message} />
                  )} />
                  <IconButton onClick={() => unitConversions.remove(i)}><DeleteOutlined fontSize="small" /></IconButton>
                </Stack>
              ))}
              <Box>
                <Button size="small" startIcon={<Add />} onClick={() => unitConversions.append({ unitOfMeasureId: '', factorToPrimary: '1' })}>
                  Adaugă conversie
                </Button>
              </Box>
            </Stack>

            {type === 'Set' && (
              <>
                <Divider><Typography variant="overline" color="text.secondary">Componente set</Typography></Divider>
                <Stack spacing={1}>
                  {setComponents.fields.map((f, i) => (
                    <Stack key={f.id} direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
                      <Controller name={`setComponents.${i}.componentArticleId`} control={control} render={({ field }) => (
                        <Autocomplete
                          fullWidth options={componentOptions.map((a) => a.id)}
                          getOptionLabel={(id) => { const a = findArticle(id); return a ? `${a.code} — ${a.shortDescription}` : id }}
                          value={field.value || null}
                          onChange={(_, v) => field.onChange(v ?? '')}
                          renderInput={(params) => (
                            <TextField {...params} label="Articol componentă"
                              error={!!errors.setComponents?.[i]?.componentArticleId} helperText={errors.setComponents?.[i]?.componentArticleId?.message} />
                          )}
                        />
                      )} />
                      <Controller name={`setComponents.${i}.quantity`} control={control} render={({ field }) => (
                        <TextField {...field} type="number" label="Cantitate" sx={{ width: 160 }}
                          error={!!errors.setComponents?.[i]?.quantity} helperText={errors.setComponents?.[i]?.quantity?.message} />
                      )} />
                      <IconButton onClick={() => setComponents.remove(i)}><DeleteOutlined fontSize="small" /></IconButton>
                    </Stack>
                  ))}
                  <Box>
                    <Button size="small" startIcon={<Add />} onClick={() => setComponents.append({ componentArticleId: '', quantity: '1' })}>
                      Adaugă componentă
                    </Button>
                  </Box>
                </Stack>
              </>
            )}

            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
